import { CardTitle } from '../../components/ui/card'
import { BillingCardData } from './useBillingInfo'

export type ConsumerStatus =
  | 'Trial'
  | 'Active'
  | 'Warning'
  | 'Suspended'
  | 'Inactive'

interface AccountHeaderProps {
  cardData: BillingCardData | null
}

const getStatusBadgeClass = (status?: ConsumerStatus | string) => {
  switch (status) {
    case 'Active':
      return 'badge-light-success text-success'
    case 'Trial':
      return 'badge-light-primary text-primary'
    case 'Warning':
      return 'badge-light-warning text-warning'
    case 'Suspended':
      return 'badge-light-danger text-danger'
    case 'Inactive':
      return 'badge-light-dark text-dark'
    default:
      return 'badge-light-secondary text-muted'
  }
}

const formatCurrency = (amount?: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount ?? 0)
}

const AccountHeader = ({ cardData }: AccountHeaderProps) => {
  const statusClass = getStatusBadgeClass(cardData?.status)

  return (
    <div className='card mb-6 mb-xl-12'>
      <div className='card-body p-10'>
        <div className='flex-wrap '>
          <CardTitle>Billing</CardTitle>
          <span className='fs-6 text-gray-600 fw-bold'>This information is updated once a day</span>
          <div className='flex-grow-1 mt-5'>
            <div className='d-flex flex-wrap'>
              {/* Available Balance */}
              <div className='border border-gray-300 border-dashed rounded min-w-175px py-5 px-6 me-8 mb-4'>
                <div className='fs-1 fw-bolder text-gray-800'>
                  {formatCurrency(cardData?.availableBalance)}
                </div>
                <div className='fw-semibold fs-5 text-gray-500'>
                  Available Balance
                </div>
              </div>

              {/* Month Usage */}
              <div className='border border-gray-300 border-dashed rounded min-w-175px py-5 px-6 me-8 mb-4'>
                <div className='fs-1 fw-bolder text-gray-800'>
                  {formatCurrency(cardData?.monthToDateUsage?.totalCost)}
                </div>
                <div className='fw-semibold fs-5 text-gray-500'>
                  Month to Date Usage
                </div>
              </div>

              {/* Account Status */}
              <div className='border border-gray-300 border-dashed rounded min-w-175px py-5 px-6 me-8 mb-4'>
                <div className='d-flex align-items-center'>
                  <div
                    className={`badge ${statusClass} fs-6 fw-bold px-4 py-2`}
                  >
                    {cardData?.status ?? 'Unknown'}
                  </div>
                </div>
                <div className='fw-semibold fs-5 text-gray-500 mt-3'>
                  Account Status
                </div>
              </div>
            </div>

            {/* Notice */}
            {(cardData?.status === 'Inactive' ||
              cardData?.status === 'Suspended' ||
              cardData?.status === 'Trial' ||
              cardData?.status === 'Warning') && (
                <div className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-6 mt-4'>
                  <div className='fw-bold fs-5'>
                    <span className='text-gray-700 fw-bolder'>Note: </span>
                    <span className='text-gray-600'>
                      Please add credits to activate your account.
                    </span>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccountHeader
